import {
  Accommodation,
  Attendant,
  WebsiteEvent,
  Guest,
  TimestampedModel,
  Page,
  Registry,
  Section,
  Video,
  Photo,
  Theme,
} from "./modelTypes";

// Params used to select a collection when just
// the name is not enough
export type ReduxCollectionParams = {
  page?: number;
  sections?: {
    page: number;
  };
};

// Collection as it exists within redux, not the "collection" that we
// select and use
export type ReduxCollection = {
  fetchTime: number | null;
  ids: (string | number)[];
  isFetching: boolean;
  shouldFetch: boolean;
  params: ReduxCollectionParams;
};

// Each action contains these fields
export type ReduxActionEntry = {
  errors: Record<string, string>;
  pending: boolean;
};

export type ReduxDeleteEntry = ReduxActionEntry;

export type ReduxUpdateEntry<T extends TimestampedModel> = {
  data: T;
  patch: boolean;
  silent: boolean;
} & ReduxActionEntry;

export type ReduxCreateEntry<T extends TimestampedModel> = {
  data: T;
  params: Record<string, string>;
} & ReduxActionEntry;

// This is a "resource" within redux, not the translated data that
// our components use
export type ReduxResource<T extends TimestampedModel> = {
  collections: ReduxCollection[];
  byId: Record<string | number, T>;
  name: string;
  staged: {
    create: ReduxCreateEntry<T>[];
    delete: Record<string | number, ReduxDeleteEntry>;
    update: Record<string | number, ReduxUpdateEntry<T>>;
  };
};

export enum ReduxResourceKeys {
  Accommodations = "accommodations",
  Attendants = "attendants",
  Registries = "registries",
  RSVPs = "rsvps",
  Events = "events",
  Guests = "guests",
  Invites = "invites",
  Pages = "pages",
  Photos = "photos",
  Sections = "sections",
  Videos = "videos",
}

export type ReduxResourceContainer = {
  [key in ReduxResourceKeys]: ReduxResource<any>;
} & {
  pages: ReduxResource<Page>;
  sections: ReduxResource<Section>;
  events: ReduxResource<WebsiteEvent>;
  guests: ReduxResource<Guest>;
  rsvps: ReduxResource<any>;
  registries: ReduxResource<Registry>;
  attendants: ReduxResource<Attendant>;
  accommodations: ReduxResource<Accommodation>;
  // tasks: ReduxResource<any>,
  invites: ReduxResource<any>;
  comments: ReduxResource<any>;
  photos: ReduxResource<Photo>;
  videos: ReduxResource<Video>;
};

export type RootState = {
  resources: ReduxResourceContainer;
  router: {
    route: {
      name: string;
    };
  };
  theme: Theme;
  activeModal: {
    props: any;
    type: string;
  };
};

// For media, byId uses the UUID as opposed to the PK. For the new photo
// relations, we need to search by PK while we switch over
const getPhotoByPkFromById = (
  byId: Record<string, Photo>,
  pk: number,
): Photo | null =>
  Object.values(byId).find((photo: Photo) => photo.pk === pk) || null;

export const getPhotoByPk = (
  state: RootState,
  photoPk: number | null | undefined,
) =>
  photoPk ? getPhotoByPkFromById(state.resources.photos.byId, photoPk) : null;
